/**
 * V55.47: 캘린더 일별 이벤트 합성 유틸리티
 *
 * 근로일정 + 승인된 휴가/OT를 합성하여 캘린더에 표시할 이벤트 목록 생성
 *
 * **합성 규칙:**
 * 1. 종일 휴가 (fullDay): 근로일정 대체 → 휴가 이벤트만 표시
 * 2. 오전반차 (morningHalf): startTime~(endTime-4h) 휴가, (endTime-4h)~endTime 근무
 * 3. 오후반차 (afternoonHalf): startTime~(endTime-4h) 근무, (endTime-4h)~endTime 휴가
 *    (퍼스널힐링데이 포함 — BE 가 unit='afternoonHalf' 강제)
 * 4. OT (overtime/weekendWork/holidayWork): 기존 일정 유지 + OT 이벤트 추가
 * 5. hourly 휴가: 근무 유지 + 휴가 라벨 추가 (시간 정보 없음)
 *
 * V55.47.1 패치:
 * - hourly 휴가가 근무를 대체하지 않도록 수정
 * - 휴가/OT 없는 평일에도 work 이벤트 반환 보장
 *
 * V70 패치:
 * - 반차 경계를 schedule.lunchEnd / schedule.lunchStart 대신
 *   "퇴근시간 - 4시간" (endTime - 4h) 로 통일
 * - 사유: lunchEnd 는 직원별 점심 종료시간(기본 13:00)이라 직원별 근무시간
 *   (10~19, 9~18 등) 차이를 반영하지 못해 오후반차 시작시간이 잘못 표기됨
 * - BE calculateLeaveInterval (functions/src/modules/leave/requests.ts) 의
 *   "출근+5시간" 정책과 정합되며, 8시간 근무 표준에서 동일 결과를 보장
 */

import { DailySchedule } from '../types/workSchedule';
import { ApprovedLeaveEvent, ApprovedOvertimeEvent } from '../hooks/useApprovedWorkSchedule';

/**
 * 캘린더에 표시할 이벤트 타입
 */
export type CalendarEventType = 'work' | 'leave' | 'overtime';

/**
 * 캘린더 이벤트
 */
export interface SynthesizedCalendarEvent {
  type: CalendarEventType;
  title: string;
  startTime: string;  // HH:mm
  endTime: string;    // HH:mm
  /** OT가 익일 종료인 경우 */
  endDateOffset?: 0 | 1;
  /** 원본 데이터 참조 */
  sourceId?: string;
}

/**
 * OT 타입 라벨 맵
 */
const OT_TYPE_LABELS: Record<string, string> = {
  overtime: '연장근무',
  weekendWork: '휴일근무',
  holidayWork: '휴일근무',     // V247: 기존 데이터 하위호환
};

/**
 * V70: 반차 경계 시간 계산 — "퇴근시간 - 4시간"
 *
 * 오전반차 종료 = 오후반차 시작 = endTime - 4h
 *
 * 예)
 * - 10:00~19:00 → 15:00
 * - 09:00~18:00 → 14:00
 *
 * @param endTime "HH:mm" 형식의 퇴근시간
 * @returns "HH:mm" 형식의 반차 경계 (계산 실패 시 빈 문자열)
 */
/**
 * V64: 근무 구간에서 hourly 휴가 구간을 제외하여 분할된 근무 세그먼트 반환
 *
 * 예) work 10:00~19:00, leaves [{14:00~16:00}]
 *  → [{10:00~14:00}, {16:00~19:00}]
 *
 * 예) work 10:00~19:00, leaves [{10:00~11:00}]
 *  → [{11:00~19:00}]
 *
 * 예) work 10:00~19:00, leaves [{18:00~19:00}]
 *  → [{10:00~18:00}]
 *
 * 예) work 10:00~19:00, leaves [{10:00~11:00}, {14:00~16:00}]
 *  → [{11:00~14:00}, {16:00~19:00}]
 */
function subtractLeaveIntervals(
  workStart: string,
  workEnd: string,
  leaves: Array<{ start: string; end: string }>,
): Array<{ start: string; end: string }> {
  const toMin = (hhm: string): number => {
    const [h, m] = hhm.split(':').map(Number);
    return h * 60 + m;
  };
  const toHHmm = (min: number): string => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const workStartMin = toMin(workStart);
  const workEndMin = toMin(workEnd);
  if (workEndMin <= workStartMin) return [];

  // 휴가 구간을 근무 범위로 클램프 + 정렬
  const intervals = leaves
    .map((l) => ({
      start: Math.max(toMin(l.start), workStartMin),
      end: Math.min(toMin(l.end), workEndMin),
    }))
    .filter((i) => i.start < i.end)
    .sort((a, b) => a.start - b.start);

  // 겹치는 구간 병합
  const merged: Array<{ start: number; end: number }> = [];
  for (const iv of intervals) {
    if (merged.length && iv.start <= merged[merged.length - 1].end) {
      merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, iv.end);
    } else {
      merged.push({ ...iv });
    }
  }

  // 근무 - 휴가 = 근무 세그먼트
  const segments: Array<{ start: string; end: string }> = [];
  let cursor = workStartMin;
  for (const iv of merged) {
    if (iv.start > cursor) {
      segments.push({ start: toHHmm(cursor), end: toHHmm(iv.start) });
    }
    cursor = Math.max(cursor, iv.end);
  }
  if (cursor < workEndMin) {
    segments.push({ start: toHHmm(cursor), end: toHHmm(workEndMin) });
  }

  return segments;
}

/**
 * V64: 이벤트 시간순 정렬 (안정 정렬)
 * - 시작 시간(startTime) 오름차순
 * - startTime 없는 이벤트(전일휴가 등)는 맨 위(가장 먼저) 배치 — 해당 날짜 전체를 커버하는 속성상 자연스러움
 * - startTime 동일 시 push 순서 유지 (Array.sort는 안정 정렬 보장됨 — ES2019+)
 */
function sortEventsByStartTime(events: SynthesizedCalendarEvent[]): SynthesizedCalendarEvent[] {
  const toMin = (hhm: string): number => {
    if (!hhm) return -1; // 시간 없는 이벤트는 맨 위
    const parts = hhm.split(':');
    if (parts.length < 2) return -1;
    const h = Number(parts[0]);
    const m = Number(parts[1]);
    if (Number.isNaN(h) || Number.isNaN(m)) return -1;
    return h * 60 + m;
  };
  return events.slice().sort((a, b) => toMin(a.startTime) - toMin(b.startTime));
}

function computeHalfDayBoundary(endTime: string | undefined | null): string {
  if (!endTime || typeof endTime !== 'string') return '';
  const parts = endTime.split(':');
  if (parts.length < 2) return '';
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const total = h * 60 + m - 4 * 60;
  if (total < 0) return '';
  const newH = Math.floor(total / 60);
  const newM = total % 60;
  return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
}

/**
 * 일별 캘린더 이벤트 합성
 *
 * @param date - 대상 날짜 (YYYY-MM-DD)
 * @param schedule - 해당 날짜의 근로일정 (base + override 적용 후)
 * @param leaves - 해당 월의 승인된 휴가 목록 (BE에서 받은 전체)
 * @param overtimes - 해당 월의 승인된 OT 목록 (BE에서 받은 전체)
 * @returns 해당 날짜에 표시할 이벤트 목록
 */
export function synthesizeDailyEvents(
  date: string,
  schedule: DailySchedule | null | undefined,
  leaves: ApprovedLeaveEvent[],
  overtimes: ApprovedOvertimeEvent[]
): SynthesizedCalendarEvent[] {
  const events: SynthesizedCalendarEvent[] = [];

  // 해당 날짜의 휴가/OT 필터링
  const dayLeaves = leaves.filter((l) => l.date === date);
  const dayOvertimes = overtimes.filter((o) => o.date === date);

  // 근로일정이 없으면 휴가/OT만 반환
  if (!schedule) {
    // 휴가만 표시 (V289: 근로일정 없으면 정확한 시간 불명 → 시간 미표시)
    for (const leave of dayLeaves) {
      events.push({
        type: 'leave',
        title: leave.leaveTypeName,
        startTime: '',
        endTime: '',
        sourceId: leave.id,
      });
    }
    // OT 표시
    for (const ot of dayOvertimes) {
      events.push({
        type: 'overtime',
        title: OT_TYPE_LABELS[ot.type] || '연장근무',
        startTime: ot.startTime,
        endTime: ot.endTime,
        endDateOffset: ot.endDateOffset,
        sourceId: ot.id,
      });
    }
    return sortEventsByStartTime(events);
  }

  // === 휴가 처리 ===

  // 1. 종일 휴가 (fullDay) 체크
  const fullDayLeave = dayLeaves.find((l) => l.unit === 'fullDay');
  if (fullDayLeave) {
    // 종일 휴가는 근로일정 대체 (V289: 전일 휴가는 시간 미표시 — 타입명만 노출)
    events.push({
      type: 'leave',
      title: fullDayLeave.leaveTypeName,
      startTime: '',
      endTime: '',
      sourceId: fullDayLeave.id,
    });

    // OT는 휴가와 별개로 추가 가능
    for (const ot of dayOvertimes) {
      events.push({
        type: 'overtime',
        title: OT_TYPE_LABELS[ot.type] || '연장근무',
        startTime: ot.startTime,
        endTime: ot.endTime,
        endDateOffset: ot.endDateOffset,
        sourceId: ot.id,
      });
    }

    return sortEventsByStartTime(events);
  }

  // 2. V81.3: 반차(morningHalf/afternoonHalf) + hourly 통합 처리
  //    — 기존: mutually exclusive 분기 (반차 있으면 hourly 누락)
  //    — 변경: 모든 leave를 interval로 변환 → subtractLeaveIntervals 로 한번에 근무 구간 계산
  // V70: 반차 경계 = 퇴근시간 - 4시간 (lunchEnd/lunchStart 사용 금지)
  //      퍼스널힐링데이는 BE 가 unit='afternoonHalf' 강제하므로 이 분기에 함께 처리됨
  const halfDayBoundary = computeHalfDayBoundary(schedule.endTime);

  type LeaveInterval = {
    leave: ApprovedLeaveEvent;
    start: string;
    end: string;
    title: string;
  };
  const leaveIntervals: LeaveInterval[] = [];

  for (const leave of dayLeaves) {
    if (leave.unit === 'morningHalf' && halfDayBoundary) {
      leaveIntervals.push({
        leave,
        start: schedule.startTime,
        end: halfDayBoundary,
        title: leave.leaveTypeName,
      });
    } else if (leave.unit === 'afternoonHalf' && halfDayBoundary) {
      leaveIntervals.push({
        leave,
        start: halfDayBoundary,
        end: schedule.endTime,
        title: leave.leaveTypeName,
      });
    } else if (leave.unit === 'hourly' && leave.startTime && leave.endTime) {
      leaveIntervals.push({
        leave,
        start: leave.startTime,
        end: leave.endTime,
        title: `${leave.leaveTypeName} (${leave.startTime}~${leave.endTime})`,
      });
    }
    // 그 외(fullDay는 이미 early return에서 처리됨, hourly without times는 아래 fallback)
  }

  // 모든 leave interval 차감하여 실제 근무 구간 계산 (겹침 자동 병합)
  const workSegments = subtractLeaveIntervals(
    schedule.startTime,
    schedule.endTime,
    leaveIntervals.map((li) => ({ start: li.start, end: li.end })),
  );

  // 근무 이벤트 추가 (분할된 각 구간)
  for (const seg of workSegments) {
    events.push({
      type: 'work',
      title: '근무',
      startTime: seg.start,
      endTime: seg.end,
    });
  }

  // leave 이벤트 추가 (반차 + hourly 모두 개별 표기)
  for (const li of leaveIntervals) {
    events.push({
      type: 'leave',
      title: li.title,
      startTime: li.start,
      endTime: li.end,
      sourceId: li.leave.id,
    });
  }

  // startTime/endTime 없는 hourly 휴가(구 데이터) fallback
  const hourlyLeavesNoTime = dayLeaves.filter(
    (l) => l.unit === 'hourly' && (!l.startTime || !l.endTime),
  );
  for (const hourlyLeave of hourlyLeavesNoTime) {
    const hoursLabel = hourlyLeave.hours ? `${hourlyLeave.hours}시간` : '시간단위';
    events.push({
      type: 'leave',
      title: `${hourlyLeave.leaveTypeName} (${hoursLabel})`,
      startTime: '',
      endTime: '',
      sourceId: hourlyLeave.id,
    });
  }

  // === OT 추가 ===
  for (const ot of dayOvertimes) {
    events.push({
      type: 'overtime',
      title: OT_TYPE_LABELS[ot.type] || '연장근무',
      startTime: ot.startTime,
      endTime: ot.endTime,
      endDateOffset: ot.endDateOffset,
      sourceId: ot.id,
    });
  }

  return sortEventsByStartTime(events);
}

/**
 * 이벤트 타입별 색상 반환
 */
export function getEventTypeColor(type: CalendarEventType): {
  bg: string;
  text: string;
  border: string;
} {
  switch (type) {
    case 'work':
      return { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' }; // 녹색
    case 'leave':
      return { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' }; // 노랑
    case 'overtime':
      return { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' }; // 파랑
    default:
      return { bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' }; // 회색
  }
}
